import { useMemo } from 'react';
import { usePointLazyQuery, PointPureType } from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import { StreamType } from '@entities/media';
import { LicenseType } from '@entities/billing';

const POLLING_INTERVAL = 120000;

export type DeviceTypeType =
  | 'ANDROID'
  | 'DESKTOP'
  | 'DESKTOPBOX'
  | 'IOS'
  | 'WEBPLAYER'
  | 'ANDROIDBOX'
  | 'ALICE_SPEAKER';

export type PointType = Pick<
  PointPureType,
  | 'id'
  | 'comment'
  | 'address'
  | 'playingStatus'
  | 'isEnabled'
  | 'sleepOptions'
  | 'timezone'
  | 'amplifyOptions'
  | 'withMusicCut'
  | 'cutDuration'
  | 'adOverlapResolution'
  | 'isVideoMode'
  | 'isVideoDemo'
> & {
  stream?: Pick<StreamType, 'id' | 'title' | 'description'>;
  license?: LicenseType;
  device?: {
    id: string;
    requiredVersionUrl?: string;
    bindingCode: string;
    type: DeviceTypeType;
    volume: number;
  };
  adcampaigns?: {
    id: string;
  }[];
};

const usePoint = () => {
  const [query, queryResult] = usePointLazyQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    pollInterval: POLLING_INTERVAL,
  });

  const request = async (id: string) =>
    query({
      variables: { id },
    });

  const pointPureQuery = queryResult.data?.pointPure;

  const point: PointType | undefined = useMemo(() => {
    if (!pointPureQuery) return undefined;

    return {
      id: pointPureQuery.id,
      comment: pointPureQuery.comment,
      address: pointPureQuery.address,
      isEnabled: pointPureQuery.isEnabled,
      playingStatus: pointPureQuery.playingStatus,
      sleepOptions: pointPureQuery.sleepOptions,
      timezone: pointPureQuery.timezone,
      stream: pointPureQuery.stream ? pointPureQuery.stream : undefined,
      device: pointPureQuery.device
        ? {
            id: pointPureQuery.device.id,
            requiredVersionUrl: pointPureQuery.device.requiredVersionUrl || undefined,
            bindingCode: pointPureQuery.device.bindingCode,
            volume: pointPureQuery.device.volume,
            bindedAt: pointPureQuery.device.bindedAt,
            supportedFeatures: pointPureQuery.device.supportedFeatures,
            type: pointPureQuery.device.type as DeviceTypeType,
            group: pointPureQuery.device.group,
          }
        : undefined,
      license: pointPureQuery.license
        ? {
            id: pointPureQuery.license.id,
            autoRenewal: pointPureQuery.license.autoRenewal,
            paymentState: pointPureQuery.license.paymentState,
            expiresAt: pointPureQuery.license.expiresAt,
            modified: pointPureQuery.license.modified,
          }
        : undefined,
      amplifyOptions: {
        amplifyMediafile: pointPureQuery.amplifyOptions.amplifyMediafile,
        amplifyPromo: pointPureQuery.amplifyOptions.amplifyPromo,
        amplifyInstant: pointPureQuery.amplifyOptions.amplifyInstant,
      },
      tags: pointPureQuery.tags || [],
      adcampaigns: pointPureQuery.adcampaigns || [],
      withMusicCut: pointPureQuery.withMusicCut,
      cutDuration: pointPureQuery.cutDuration,
      adOverlapResolution: pointPureQuery.adOverlapResolution,
      fileFormat: pointPureQuery.fileFormat,
      videoStream: pointPureQuery.videoStream,
      isVideoMode: pointPureQuery.isVideoMode,
      isVideoDemo: pointPureQuery.isVideoDemo,
    };
  }, [pointPureQuery]);

  const getPoint = async (id: string): Promise<PointType | undefined> => {
    const result = await request(id);

    return result.data?.pointPure as PointType;
  };

  return {
    error: queryResult.error,
    loading: queryResult.loading,
    point,
    request,
    getPoint,
  };
};

export default usePoint;
